import React, { useState, useEffect } from 'react';
import { 
  Shield, AlertTriangle, Ban, Users, MessageSquare, 
  CheckCircle, XCircle, Clock, Eye, Trash2, 
  ChevronDown, Filter, RefreshCw, Gavel, FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ModerationPanel = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('reports');
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);
  const [suspensions, setSuspensions] = useState([]);
  const [appeals, setAppeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  
  // Dialog states
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportContent, setReportContent] = useState(null);
  const [suspensionDialog, setSuspensionDialog] = useState({ open: false, userId: null, username: null });
  const [appealDialog, setAppealDialog] = useState({ open: false, appeal: null });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchStats();
    fetchReports();
    fetchSuspensions();
    fetchAppeals();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/moderation/stats`, { headers });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchReports = async (status = null) => {
    setLoading(true);
    try {
      const params = status ? `?status=${status}` : '';
      const response = await axios.get(`${API_URL}/api/admin/moderation/reports${params}`, { headers });
      setReports(response.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuspensions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/moderation/suspensions?active_only=true`, { headers });
      setSuspensions(response.data);
    } catch (error) {
      console.error('Error fetching suspensions:', error);
    }
  };

  const fetchAppeals = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/moderation/appeals?status=pending`, { headers });
      setAppeals(response.data);
    } catch (error) {
      console.error('Error fetching appeals:', error);
    }
  };

  const handleViewReportContent = async (report) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/moderation/reports/${report.id}/content`,
        { headers }
      );
      setReportContent(response.data);
      setSelectedReport(report);
    } catch (error) {
      toast.error('Failed to load report content');
    }
  };

  const handleUpdateReport = async (reportId, status, actionTaken = null) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/moderation/reports/${reportId}`,
        { status, action_taken: actionTaken },
        { headers }
      );
      toast.success('Report updated');
      fetchReports(statusFilter);
      fetchStats();
      setSelectedReport(null);
      setReportContent(null);
    } catch (error) {
      toast.error('Failed to update report');
    }
  };

  const handleDeleteContent = async (reportId) => {
    try {
      await axios.delete(
        `${API_URL}/api/admin/moderation/reports/${reportId}/content`,
        { headers }
      );
      toast.success('Content deleted');
      fetchReports(statusFilter);
      setSelectedReport(null);
      setReportContent(null);
    } catch (error) {
      toast.error('Failed to delete content');
    }
  };

  const handleCreateSuspension = async (data) => {
    try {
      await axios.post(
        `${API_URL}/api/admin/moderation/suspensions`,
        data,
        { headers }
      );
      toast.success(`User ${data.type === 'warning' ? 'warned' : data.type === 'ban' ? 'banned' : 'suspended'}`);
      fetchSuspensions();
      fetchStats();
      setSuspensionDialog({ open: false, userId: null, username: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create suspension');
    }
  };

  const handleLiftSuspension = async (suspensionId) => {
    try {
      await axios.post(
        `${API_URL}/api/admin/moderation/suspensions/${suspensionId}/lift`,
        {},
        { headers }
      );
      toast.success('Suspension lifted');
      fetchSuspensions();
      fetchStats();
    } catch (error) {
      toast.error('Failed to lift suspension');
    }
  };

  const handleReviewAppeal = async (appealId, status, adminResponse) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/moderation/appeals/${appealId}`,
        { status, admin_response: adminResponse },
        { headers }
      );
      toast.success(`Appeal ${status}`);
      fetchAppeals();
      fetchSuspensions();
      fetchStats();
      setAppealDialog({ open: false, appeal: null });
    } catch (error) {
      toast.error('Failed to review appeal');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-yellow-100 text-yellow-800',
      reviewed: 'bg-blue-100 text-blue-800',
      actioned: 'bg-green-100 text-green-800',
      dismissed: 'bg-gray-100 text-gray-800',
    };
    return <Badge className={variants[status] || variants.pending}>{status}</Badge>;
  };

  const getReportTypeBadge = (type) => {
    const variants = {
      spam: 'bg-orange-100 text-orange-800',
      harassment: 'bg-red-100 text-red-800',
      inappropriate: 'bg-pink-100 text-pink-800',
      misinformation: 'bg-purple-100 text-purple-800',
      impersonation: 'bg-indigo-100 text-indigo-800',
      other: 'bg-gray-100 text-gray-800',
    };
    return <Badge className={variants[type] || variants.other}>{type}</Badge>;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Shield className="w-8 h-8 text-primary" />
              Moderation Panel
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage reports, suspensions, and appeals
            </p>
          </div>
          <Button onClick={() => { fetchStats(); fetchReports(statusFilter); fetchSuspensions(); fetchAppeals(); }}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-yellow-600">{stats.pending_reports}</p>
                <p className="text-sm text-muted-foreground">Pending Reports</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{stats.reports_today}</p>
                <p className="text-sm text-muted-foreground">Reports Today</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{stats.total_reports}</p>
                <p className="text-sm text-muted-foreground">Total Reports</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-orange-600">{stats.active_suspensions}</p>
                <p className="text-sm text-muted-foreground">Active Suspensions</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-600">{stats.active_bans}</p>
                <p className="text-sm text-muted-foreground">Active Bans</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-purple-600">{stats.pending_appeals}</p>
                <p className="text-sm text-muted-foreground">Pending Appeals</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{stats.total_blocks}</p>
                <p className="text-sm text-muted-foreground">User Blocks</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="reports" className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Reports
              {stats?.pending_reports > 0 && (
                <Badge variant="destructive" className="ml-1">{stats.pending_reports}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="suspensions" className="flex items-center gap-2">
              <Ban className="w-4 h-4" />
              Suspensions
            </TabsTrigger>
            <TabsTrigger value="appeals" className="flex items-center gap-2">
              <Gavel className="w-4 h-4" />
              Appeals
              {stats?.pending_appeals > 0 && (
                <Badge variant="secondary" className="ml-1">{stats.pending_appeals}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Reports Tab */}
          <TabsContent value="reports">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Content Reports</CardTitle>
                  <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); fetchReports(v); }}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="reviewed">Reviewed</SelectItem>
                      <SelectItem value="actioned">Actioned</SelectItem>
                      <SelectItem value="dismissed">Dismissed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">Loading...</div>
                ) : reports.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
                    <p>No {statusFilter} reports</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reports.map((report) => (
                      <div
                        key={report.id}
                        className="border rounded-lg p-4 hover:bg-muted/50"
                      >
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              {getReportTypeBadge(report.report_type)}
                              {getStatusBadge(report.status)}
                              <Badge variant="outline">{report.content_type}</Badge>
                            </div>
                            <p className="text-sm">
                              Reported by <strong>{report.reporter_username || 'Unknown'}</strong>
                              {' · '}
                              {formatDistanceToNow(new Date(report.created_at), { addSuffix: true })}
                            </p>
                            {report.description && (
                              <p className="text-sm text-muted-foreground">{report.description}</p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => handleViewReportContent(report)}>
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                            {report.status === 'pending' && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button size="sm">
                                    Actions
                                    <ChevronDown className="w-4 h-4 ml-1" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent>
                                  <DropdownMenuItem onClick={() => handleUpdateReport(report.id, 'dismissed')}>
                                    <XCircle className="w-4 h-4 mr-2" />
                                    Dismiss
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleUpdateReport(report.id, 'reviewed', 'Warning issued')}>
                                    <AlertTriangle className="w-4 h-4 mr-2" />
                                    Mark Reviewed
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    className="text-destructive"
                                    onClick={() => handleDeleteContent(report.id)}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete Content
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Suspensions Tab */}
          <TabsContent value="suspensions">
            <Card>
              <CardHeader>
                <CardTitle>Active Suspensions & Bans</CardTitle>
              </CardHeader>
              <CardContent>
                {suspensions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-4" />
                    <p>No active suspensions or bans</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {suspensions.map((suspension) => (
                      <div
                        key={suspension.id}
                        className="border rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <Badge className={suspension.type === 'ban' ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'}>
                                {suspension.type}
                              </Badge>
                              <strong>{suspension.username || suspension.user_id}</strong>
                            </div>
                            <p className="text-sm text-muted-foreground">{suspension.reason}</p>
                            <p className="text-xs text-muted-foreground">
                              Issued by {suspension.issued_by_username || 'Admin'}
                              {suspension.expires_at && (
                                <> · Expires {formatDistanceToNow(new Date(suspension.expires_at), { addSuffix: true })}</>
                              )}
                            </p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleLiftSuspension(suspension.id)}
                          >
                            Lift
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Appeals Tab */}
          <TabsContent value="appeals">
            <Card>
              <CardHeader>
                <CardTitle>Pending Appeals</CardTitle>
              </CardHeader>
              <CardContent>
                {appeals.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Gavel className="w-12 h-12 mx-auto mb-4" />
                    <p>No pending appeals</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {appeals.map((appeal) => (
                      <div
                        key={appeal.id}
                        className="border rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <Badge className="bg-purple-100 text-purple-800">Appeal</Badge>
                              <strong>{appeal.username || appeal.user_id}</strong>
                            </div>
                            <p className="text-sm">{appeal.reason}</p>
                            <p className="text-xs text-muted-foreground">
                              Submitted {formatDistanceToNow(new Date(appeal.created_at), { addSuffix: true })}
                            </p>
                          </div>
                          <Button 
                            size="sm"
                            onClick={() => setAppealDialog({ open: true, appeal })}
                          >
                            Review
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Report Content Dialog */}
        <Dialog open={!!selectedReport} onOpenChange={() => { setSelectedReport(null); setReportContent(null); }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Report Details</DialogTitle>
            </DialogHeader>
            {reportContent && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-muted-foreground">Report Type</Label>
                    <p>{selectedReport?.report_type}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Content Type</Label>
                    <p>{selectedReport?.content_type}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Reported By</Label>
                    <p>{selectedReport?.reporter_username}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Content Owner</Label>
                    <p>{reportContent.content_owner?.username || 'Unknown'}</p>
                  </div>
                </div>
                
                {selectedReport?.description && (
                  <div>
                    <Label className="text-muted-foreground">Reporter's Description</Label>
                    <p className="text-sm mt-1">{selectedReport.description}</p>
                  </div>
                )}

                <div className="border rounded-lg p-4 bg-muted/50">
                  <Label className="text-muted-foreground">Reported Content</Label>
                  <div className="mt-2">
                    {reportContent.content ? (
                      <pre className="text-sm whitespace-pre-wrap">
                        {JSON.stringify(reportContent.content, null, 2)}
                      </pre>
                    ) : (
                      <p className="text-muted-foreground">Content not available</p>
                    )}
                  </div>
                </div>

                {selectedReport?.status === 'pending' && reportContent.content_owner && (
                  <DialogFooter className="flex gap-2">
                    <Button variant="outline" onClick={() => handleUpdateReport(selectedReport.id, 'dismissed')}>
                      Dismiss
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        setSuspensionDialog({ 
                          open: true, 
                          userId: reportContent.content_owner.id,
                          username: reportContent.content_owner.username,
                          reportId: selectedReport.id
                        });
                      }}
                    >
                      Warn/Suspend User
                    </Button>
                    <Button variant="destructive" onClick={() => handleDeleteContent(selectedReport.id)}>
                      Delete Content
                    </Button>
                  </DialogFooter>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Suspension Dialog */}
        <SuspensionDialog 
          open={suspensionDialog.open}
          userId={suspensionDialog.userId}
          username={suspensionDialog.username}
          reportId={suspensionDialog.reportId}
          onClose={() => setSuspensionDialog({ open: false, userId: null, username: null })}
          onSubmit={handleCreateSuspension}
        />

        {/* Appeal Review Dialog */}
        <AppealReviewDialog
          open={appealDialog.open}
          appeal={appealDialog.appeal}
          onClose={() => setAppealDialog({ open: false, appeal: null })}
          onSubmit={handleReviewAppeal}
        />
      </div>
    </div>
  );
};

// Suspension Dialog Component
const SuspensionDialog = ({ open, userId, username, reportId, onClose, onSubmit }) => {
  const [type, setType] = useState('warning');
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState(7);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    setLoading(true);
    await onSubmit({
      user_id: userId,
      type,
      reason,
      duration_days: type === 'suspension' ? duration : null,
      related_report_id: reportId || null
    });
    setLoading(false);
    setType('warning');
    setReason('');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Issue Action to {username}</DialogTitle>
          <DialogDescription>
            Choose the appropriate action for this user
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Action Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="warning">Warning (no restriction)</SelectItem>
                <SelectItem value="suspension">Suspension (temporary)</SelectItem>
                <SelectItem value="ban">Ban (permanent)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {type === 'suspension' && (
            <div className="space-y-2">
              <Label>Duration (days)</Label>
              <Input 
                type="number" 
                min={1} 
                max={365} 
                value={duration} 
                onChange={(e) => setDuration(parseInt(e.target.value))}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>Reason</Label>
            <Textarea
              placeholder="Explain why this action is being taken..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Processing...' : 'Confirm Action'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Appeal Review Dialog Component
const AppealReviewDialog = ({ open, appeal, onClose, onSubmit }) => {
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);

  const handleDecision = async (status) => {
    if (!response.trim()) {
      toast.error('Please provide a response');
      return;
    }
    setLoading(true);
    await onSubmit(appeal.id, status, response);
    setLoading(false);
    setResponse('');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Review Appeal</DialogTitle>
        </DialogHeader>
        {appeal && (
          <div className="space-y-4">
            <div>
              <Label className="text-muted-foreground">User</Label>
              <p>{appeal.username}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Appeal Reason</Label>
              <p className="text-sm">{appeal.reason}</p>
            </div>
            <div className="space-y-2">
              <Label>Your Response</Label>
              <Textarea
                placeholder="Explain your decision..."
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button 
                variant="destructive" 
                onClick={() => handleDecision('denied')}
                disabled={loading}
              >
                Deny Appeal
              </Button>
              <Button 
                onClick={() => handleDecision('approved')}
                disabled={loading}
              >
                Approve Appeal
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ModerationPanel;
